/**
 * 勘定科目別の予実(2026-10-23・Board)。2027年度予算 v8 の「区分 > 科目① > 科目②」の形で、
 * 予算・実績・差異・予算比・対売上比・見込みを並べる。集計は accountPl.ts、読み込みは accountPlRepo.ts。
 *
 *  - 期間: トップバーの月を基準に「月 / 四半期 / 年度累計 / 年度合計」(年度の始まりは settings/fiscal)
 *  - 事業部: 全社 または 1事業部
 *  - PC は表、スマホ(768px以下)は1科目1行のリスト(横スクロールなし・2026-09-07 指示)
 *  - 将来は「勘定科目別」と「項目別」を切り替えられるようにする(2026-10-23 発注者: 理想)。今は勘定科目別のみ
 *
 * 書き込みはしない(表示のみ)
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMasters } from '@core/masters/MastersProvider';
import { usePeriod } from '@core/components/PeriodProvider';
import { useToast } from '@core/components/Toast';
import { achievementRate, formatDiff, formatYen } from '@core/lib/money';
import { useIsMobile } from '@core/lib/useIsMobile';
import type { Budget, DepartmentId, Transaction } from '@core/lib/types';
import {
  PERIOD_OPTIONS,
  PROFIT_LABELS,
  buildAccountPl,
  periodMonths,
  ratioToSales,
  type AccountPl,
  type Amounts,
  type PeriodMode,
  type ProfitKey,
  type SectionKey,
} from './accountPl';
import { fetchBudgetsForMonths, fetchPlTransactionsForMonths } from './accountPlRepo';

/** 区分の合計のあとに出す利益の行(v8 と同じ位置) */
const PROFITS_AFTER: Partial<Record<SectionKey, ProfitKey[]>> = {
  cogs: ['gross'],
  sga: ['operating'],
  non_op_expense: ['ordinary'],
  extra_loss: ['pretax', 'tax', 'net'],
};

type Loaded = { transactions: Transaction[]; budgets: Budget[]; budgetError: boolean };

const isZero = (a: Amounts) => a.budget === 0 && a.actual === 0 && a.forecast === 0;

/** 予算比。予算が0以下(赤字の利益など)のときは割合に意味がないので出さない */
function rateText(budget: number, actual: number): string {
  if (budget <= 0) return '—';
  const r = achievementRate(budget, actual);
  return r === null ? '—' : `${r}%`;
}

function diffText(diff: number): string {
  return diff === 0 ? '0' : formatDiff(diff);
}

function ratioText(value: number, sales: number): string {
  const r = ratioToSales(value, sales);
  return r === null ? '—' : `${r.toFixed(1)}%`;
}

/** 差異の色: 収益・利益は多いほど良い(緑)、費用は予算を超えると赤 */
function diffClass(diff: number, cost: boolean): string {
  if (diff === 0) return '';
  return (cost ? diff < 0 : diff > 0) ? 'pos' : 'neg';
}

export default function AccountPlPage() {
  const { accounts, subAccounts, departments, fiscal, loading: mastersLoading } = useMasters();
  const { month } = usePeriod();
  const toast = useToast();
  const mobile = useIsMobile();
  const [mode, setMode] = useState<PeriodMode>('month');
  const [departmentId, setDepartmentId] = useState<DepartmentId | ''>('');
  const [hideZero, setHideZero] = useState(true);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);

  const period = useMemo(() => periodMonths(mode, month, fiscal.start_month), [mode, month, fiscal.start_month]);

  // 読み込みの追い越し対策: 条件を素早く切り替えたとき、遅れて返った古い結果で上書きしない
  const requestNo = useRef(0);

  const load = useCallback(async () => {
    // マスタ(年度の始まり月)を読み終わる前は読まない(既定の月で1回余分に読んでしまうため)
    if (mastersLoading) return;
    const no = ++requestNo.current;
    setLoading(true);
    try {
      const dept = departmentId || null;
      const [transactions, budgetResult] = await Promise.all([
        fetchPlTransactionsForMonths(dept, period.months),
        fetchBudgetsForMonths(dept, period.months, fiscal.start_month).then(
          (b) => ({ budgets: b, error: false }),
          // 予算を読む権限が無い人(経理・管理者・その事業部の人以外)は、予算を出さずに実績だけ出す
          () => ({ budgets: [] as Budget[], error: true }),
        ),
      ]);
      if (no !== requestNo.current) return;
      setLoaded({ transactions, budgets: budgetResult.budgets, budgetError: budgetResult.error });
    } catch (e) {
      if (no !== requestNo.current) return;
      toast(e instanceof Error ? e.message : '予実を読み込めませんでした');
      setLoaded(null);
    } finally {
      if (no === requestNo.current) setLoading(false);
    }
  }, [mastersLoading, departmentId, period, fiscal.start_month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const pl: AccountPl | null = useMemo(
    () =>
      loaded
        ? buildAccountPl({
            accounts,
            subAccounts,
            transactions: loaded.transactions,
            budgets: loaded.budgets,
            months: period.months,
          })
        : null,
    [loaded, accounts, subAccounts, period.months],
  );

  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const allSubIds = useMemo(
    () =>
      pl
        ? pl.sections.flatMap((s) =>
            s.accounts.filter((r) => r.subs.some((x) => x.subAccountId !== null)).map((r) => r.account.id),
          )
        : [],
    [pl],
  );
  const deptName = departmentId ? (departments.find((d) => d.id === departmentId)?.name ?? departmentId) : '全社';

  return (
    <section className="screen acct-pl">
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          勘定科目別 予実({deptName}・{period.label})
          <Link to="/pl" className="txn-link" style={{ marginLeft: 'auto', fontWeight: 400 }}>
            5行の全社PLへ
          </Link>
        </h3>

        <div className="acct-toolbar">
          <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value as DepartmentId | '')}>
              <option value="">全社</option>
              {departments
                .filter((d) => d.active !== false)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
            </select>
            <span className="chips" style={{ margin: 0 }}>
              {PERIOD_OPTIONS.map((o) => (
                <button key={o.value} className={`chip${mode === o.value ? ' on' : ''}`} onClick={() => setMode(o.value)}>
                  {o.label}
                </button>
              ))}
            </span>
          </span>
          <span style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto', margin: 0, cursor: 'pointer', fontSize: 12.5 }}>
              <input
                type="checkbox"
                style={{ margin: 0, width: 15, height: 15 }}
                checked={hideZero}
                onChange={(e) => setHideZero(e.target.checked)}
              />
              金額のない科目を隠す
            </label>
            <button
              className="chip"
              onClick={() => setOpen(open.size > 0 ? new Set() : new Set(allSubIds))}
              disabled={allSubIds.length === 0}
            >
              {open.size > 0 ? '科目②を閉じる' : '科目②をすべて開く'}
            </button>
          </span>
        </div>

        {loaded?.budgetError && (
          <p className="hint" style={{ margin: '0 0 8px' }}>
            予算を見る権限がないため、予算の列は空です(予算は経理・管理者・その事業部の人が見られます)。
          </p>
        )}
        {pl && !mastersLoading && pl.unknownAccountCount > 0 && (
          <p className="hint" style={{ margin: '0 0 8px', color: 'var(--red)' }}>
            勘定科目マスタに無い科目の取引が {pl.unknownAccountCount} 件あり、この表には入っていません。
          </p>
        )}

        {loading || !pl ? (
          <p className="empty">{loading ? '読み込み中…' : '表示できるデータがありません'}</p>
        ) : mobile ? (
          <MobileList pl={pl} hideZero={hideZero} open={open} toggle={toggle} />
        ) : (
          <PcTable pl={pl} hideZero={hideZero} open={open} toggle={toggle} />
        )}

        <p className="hint" style={{ marginTop: 12 }}>
          実績 = 承認済み以降の取引を計上日の月で集計(支払い前を含む)。見込み = 申請中・承認待ちと、計上日の月がまだ来ていない分。
          予算は科目①単位です(科目②の予算は今後対応)。税金等は v8 と同じ概算(月ごとに 税引前 × 率、赤字の月は0)。
          区分は勘定科目マスタの「項目」(売上・原価・販管・外収・外費・特利・特損)で決まります。
        </p>
      </div>
    </section>
  );
}

type ViewProps = { pl: AccountPl; hideZero: boolean; open: Set<string>; toggle: (id: string) => void };

function PcTable({ pl, hideZero, open, toggle }: ViewProps) {
  const sales = pl.sales.actual;
  const cells = (a: Amounts, cost: boolean, strong = false) => {
    const diff = a.actual - a.budget;
    return (
      <>
        <td className="num">{formatYen(a.budget)}</td>
        <td className="num" style={{ fontWeight: strong ? 700 : 400 }}>
          {formatYen(a.actual)}
        </td>
        <td className={`num ${diffClass(diff, cost)}`}>{diffText(diff)}</td>
        <td className="num">{rateText(a.budget, a.actual)}</td>
        <td className="num hint">{ratioText(a.actual, sales)}</td>
        <td className="num hint">{a.forecast === 0 ? '—' : formatYen(a.forecast)}</td>
      </>
    );
  };

  return (
    <table className="no-stack acct-table">
      <thead>
        <tr>
          <th style={{ width: '30%' }}>勘定科目</th>
          <th className="num">予算</th>
          <th className="num">実績</th>
          <th className="num">差異</th>
          <th className="num">予算比</th>
          <th className="num">売上比</th>
          <th className="num">見込み</th>
        </tr>
      </thead>
      <tbody>
        {pl.sections.map((s) => {
          const rows = s.accounts.filter((r) => !hideZero || !isZero(r.amounts));
          return (
            <Fragment key={s.key}>
              <tr className="sec">
                <td colSpan={7}>《{s.label}》</td>
              </tr>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="hint" style={{ paddingLeft: 26 }}>
                    金額のある科目はありません
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const subs = r.subs.filter((x) => !hideZero || x.actual !== 0 || x.forecast !== 0);
                // 開けるのは、表示する科目②(未選択の行を除く)があるときだけ
                const canOpen = subs.some((x) => x.subAccountId !== null);
                const isOpen = canOpen && open.has(r.account.id);
                return (
                  <Fragment key={r.account.id}>
                    <tr
                      className={canOpen ? 'pl-row' : undefined}
                      onClick={canOpen ? () => toggle(r.account.id) : undefined}
                      style={canOpen ? { cursor: 'pointer' } : undefined}
                    >
                      <td>
                        <div className="pl-name" style={{ paddingLeft: 8 }}>
                          <span className={`chev${isOpen ? ' open' : ''}`}>{canOpen ? '▶' : ''}</span>
                          {r.account.name}
                          {!r.account.active && <span className="hint">(無効)</span>}
                        </div>
                      </td>
                      {cells(r.amounts, s.cost)}
                    </tr>
                    {isOpen &&
                      subs.map((x) => (
                        <tr key={`${r.account.id}:${x.subAccountId ?? '-'}`} className="sub">
                          <td>{x.name}</td>
                          <td className="num hint">—</td>
                          <td className="num">{formatYen(x.actual)}</td>
                          <td className="num hint">—</td>
                          <td className="num hint">—</td>
                          <td className="num hint">{ratioText(x.actual, sales)}</td>
                          <td className="num hint">{x.forecast === 0 ? '—' : formatYen(x.forecast)}</td>
                        </tr>
                      ))}
                    {isOpen && subs.length === 0 && (
                      <tr className="sub">
                        <td colSpan={7} className="hint">
                          金額のある科目②はありません
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              <tr className="total">
                <td>{s.label} 合計</td>
                {cells(s.total, s.cost, true)}
              </tr>
              {(PROFITS_AFTER[s.key] ?? []).map((k) => (
                <tr key={k} className={k === 'tax' ? 'tax' : 'profit'}>
                  <td>{PROFIT_LABELS[k]}</td>
                  {cells(pl.profits[k], k === 'tax', k !== 'tax')}
                </tr>
              ))}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function MobileList({ pl, hideZero, open, toggle }: ViewProps) {
  const sales = pl.sales.actual;
  const line = (a: Amounts, cost: boolean) => {
    const diff = a.actual - a.budget;
    return (
      <div className="acct-sub-line">
        <span>予算 {formatYen(a.budget)}</span>
        <span className={diffClass(diff, cost)}>差異 {diffText(diff)}</span>
        <span>予算比 {rateText(a.budget, a.actual)}</span>
        <span>売上比 {ratioText(a.actual, sales)}</span>
        {a.forecast !== 0 && <span>見込み {formatYen(a.forecast)}</span>}
      </div>
    );
  };

  return (
    <div className="acct-list">
      {pl.sections.map((s) => {
        const rows = s.accounts.filter((r) => !hideZero || !isZero(r.amounts));
        return (
          <Fragment key={s.key}>
            <div className="acct-sec">{s.label}</div>
            {rows.map((r) => {
              const subs = r.subs.filter((x) => !hideZero || x.actual !== 0 || x.forecast !== 0);
              // 開けるのは、表示する科目②(未選択の行を除く)があるときだけ
              const canOpen = subs.some((x) => x.subAccountId !== null);
              const isOpen = canOpen && open.has(r.account.id);
              return (
                <Fragment key={r.account.id}>
                  <button
                    type="button"
                    className="acct-item"
                    onClick={canOpen ? () => toggle(r.account.id) : undefined}
                    disabled={!canOpen}
                  >
                    <div className="acct-main">
                      <span>
                        {canOpen && <span className={`chev${isOpen ? ' open' : ''}`}>▶</span>}
                        {r.account.name}
                      </span>
                      <span className="num">{formatYen(r.amounts.actual)}</span>
                    </div>
                    {line(r.amounts, s.cost)}
                  </button>
                  {isOpen &&
                    subs.map((x) => (
                      <div key={`${r.account.id}:${x.subAccountId ?? '-'}`} className="acct-item acct-child">
                        <div className="acct-main">
                          <span>{x.name}</span>
                          <span className="num">{formatYen(x.actual)}</span>
                        </div>
                      </div>
                    ))}
                </Fragment>
              );
            })}
            <div className="acct-item acct-total">
              <div className="acct-main">
                <span>{s.label} 合計</span>
                <span className="num">{formatYen(s.total.actual)}</span>
              </div>
              {line(s.total, s.cost)}
            </div>
            {(PROFITS_AFTER[s.key] ?? []).map((k) => (
              <div key={k} className={`acct-item ${k === 'tax' ? 'acct-total' : 'acct-profit'}`}>
                <div className="acct-main">
                  <span>{PROFIT_LABELS[k]}</span>
                  <span className="num">{formatYen(pl.profits[k].actual)}</span>
                </div>
                {line(pl.profits[k], k === 'tax')}
              </div>
            ))}
          </Fragment>
        );
      })}
    </div>
  );
}
